import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, BookOpen } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { kbApi, type KBArticle } from '../services/api'

const CATEGORY_LABELS: Record<string, string> = {
  'getting-started': 'Getting Started',
  auth: 'Authentication',
  'ticket-workflow': 'Ticket Workflow',
  attachments: 'Attachments',
  billing: 'Billing',
}

function categoryLabel(cat: string): string {
  return CATEGORY_LABELS[cat] ?? cat.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function HelpArticle() {
  const { articleId } = useParams<{ articleId: string }>()
  const navigate = useNavigate()
  const [article, setArticle] = useState<KBArticle | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!articleId) return
    setLoading(true)
    kbApi
      .getArticle(articleId)
      .then((data) => {
        setArticle(data)
        setError(null)
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Article not found')
      })
      .finally(() => setLoading(false))
  }, [articleId])

  return (
    <div className="max-w-3xl mx-auto">
      <button
        onClick={() => navigate('/help')}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Help Center
      </button>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error || !article ? (
        <div className="text-center py-16">
          <p className="text-red-600 mb-3 text-sm">{error || 'Article not found'}</p>
          <button
            onClick={() => navigate('/help')}
            className="text-sm text-brand-500 hover:text-brand-600 font-medium"
          >
            Back to Help Center
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8">
          {/* Meta */}
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-4 h-4 text-brand-500" />
            <span className="text-xs font-medium text-brand-600 bg-brand-50 px-2 py-0.5 rounded-full">
              {categoryLabel(article.category)}
            </span>
          </div>

          <h1 className="text-xl font-bold text-gray-900 mb-2">{article.title}</h1>

          <p className="text-xs text-gray-400 mb-6">
            Updated {formatDate(article.updated_at)}
          </p>

          {/* Body */}
          <div className="prose prose-sm max-w-none text-gray-700 prose-headings:font-semibold prose-headings:text-gray-900 prose-a:text-brand-500 prose-a:no-underline hover:prose-a:underline prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded">
            <ReactMarkdown>{article.content}</ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  )
}
